import { mat4, type Mat4 } from '../math/mat4'

export type MatrixLayout = 'column-major' | 'row-major' | 'unknown'

/**
 * MediaPipe's `Matrix.data` for the facial transformation matrix has been observed both
 * column-major and row-major across versions/platforms. For a rigid transform the last row
 * is [0 0 0 1] and the translation is non-zero, so we can tell them apart per sample:
 *  - column-major: data[12..14] = translation, data[3], data[7], data[11] ≈ 0
 *  - row-major:    data[3], data[7], data[11] = translation, data[12..14] ≈ 0
 */
export function detectMatrixLayout(data: ArrayLike<number>, eps = 1e-4): MatrixLayout {
  if (data.length !== 16) return 'unknown'
  const colTrans = Math.abs(data[12]!) + Math.abs(data[13]!) + Math.abs(data[14]!)
  const rowTrans = Math.abs(data[3]!) + Math.abs(data[7]!) + Math.abs(data[11]!)
  if (colTrans > eps && rowTrans <= eps) return 'column-major'
  if (rowTrans > eps && colTrans <= eps) return 'row-major'
  return 'unknown'
}

/**
 * Sticky layout detector: decides once from the first unambiguous sample and then applies
 * the same conversion for every frame (so a degenerate frame cannot flip the interpretation).
 */
export class HeadMatrixNormalizer {
  private _layout: MatrixLayout = 'unknown'
  private tmp: Mat4 = mat4.create()

  get layout(): MatrixLayout {
    return this._layout
  }

  /** Force a layout (e.g. from a known SDK version). */
  setLayout(layout: MatrixLayout): void {
    this._layout = layout
  }

  /** Copies `data` into `out` as a column-major Mat4. */
  normalize(out: Mat4, data: ArrayLike<number>): Mat4 {
    if (this._layout === 'unknown') this._layout = detectMatrixLayout(data)
    if (this._layout === 'row-major') {
      mat4.copy(this.tmp, data)
      return mat4.transpose(out, this.tmp)
    }
    return mat4.copy(out, data)
  }

  reset(): void {
    this._layout = 'unknown'
  }
}
